import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { getSearchParam } from 'helpers/getSearchParam';
import PropTypes from 'prop-types';

import {
  Bus,
  getPromotionCategoryList,
  getPromotionsList,
  isEmptyCollection,
  isEmptyObject,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import { HeaderTitle, Label, NavigationMenu } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import SeoPageText from 'src/components/SeoPage/SeoPageText';

import PromotionPageLayout from './PromotionPageLayout';
import PromotionsCard from './PromotionsCard';
import PromotionsCardSkeleton from './PromotionsCardSkeleton';

const { PROMOTIONS } = COMPONENT_TYPES;
const ALL_PROMO_ID = 'all_promo';
const ALL_PROMO_TAB = { id: ALL_PROMO_ID, name: 'All promo' };

const usePromotionsData = (searchCategory, isInitialLoadWithQueryParam) => {
  const { authenticated } = useApplicationState();
  const { lang } = useSettings();
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const { push: navigate } = useHistory();

  const promotionsPath = SiteConfigManager.getComponentPath(PROMOTIONS);

  useEffect(() => {
    getPromotionCategoryList()
      .then(data => {
        if (isEmptyObject(data)) throw new Error('Promotion not found');

        const parsedCategories = data
          .map(category => ({
            id: String(category.id),
            name: category?.title || category.name, // title property is the one correct with translation
            orderPosition: category?.order_position,
          }))
          .sort((a, b) => {
            if (a.orderPosition === b.orderPosition) {
              return Number(a.id) - Number(b.id); // sorting case because there is no validation for the uniqueness of the orderPosition
            }
            return a.orderPosition - b.orderPosition;
          });
        setCategories(parsedCategories);
      })
      .catch(() => {
        setCategories([]);
      });
  }, [lang]);

  useEffect(() => {
    if (searchCategory && isEmptyCollection(categories)) {
      return;
    }

    const userVisibilities = ['public', authenticated ? 'registered' : 'anonymous'];
    const isExistingCategory = categories.map(category => category.id).includes(searchCategory);
    const categoryId = isExistingCategory ? parseInt(searchCategory) : undefined;

    setLoading(true);
    getPromotionsList(userVisibilities, categoryId)
      .then(promos => {
        // Redirect only on initial load with query param, existing category, and empty promotions
        if (
          isEmptyCollection(promos) &&
          isExistingCategory &&
          isInitialLoadWithQueryParam &&
          !hasLoadedOnceRef.current
        ) {
          navigate(promotionsPath);
        }
        setPromotions(promos);
        hasLoadedOnceRef.current = true;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    lang,
    authenticated,
    navigate,
    promotionsPath,
    searchCategory,
    categories,
    isInitialLoadWithQueryParam,
  ]);

  const sortedPromotions = useMemo(
    () => promotions?.sort((a, b) => a.order_position - b.order_position),
    [promotions],
  );

  return { categories, loading, promotions: sortedPromotions };
};

const CategoryMenu = ({ activeCategory, categories, onChange }) => (
  <div className="promotions-categories-menu-wrapper">
    <Label
      className={cn('navigation-menu__all-promo', activeCategory === ALL_PROMO_TAB.id && 'active')}
      message="all_promo_category"
      onClick={() => onChange(ALL_PROMO_TAB.id)}
    />
    <NavigationMenu
      className="promotions-categories-menu"
      items={categories}
      onClick={onChange}
      selectedItem={activeCategory}
    />
  </div>
);

CategoryMenu.propTypes = {
  activeCategory: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

const Promotions = () => {
  const translate = useTranslate();
  const promotionsPath = SiteConfigManager.getComponentPath(PROMOTIONS);
  const { push: navigate } = useHistory();
  const scrollRef = useRef();

  const searchCategory = getSearchParam(window.location.search, 'category') || undefined;
  const isInitialLoadWithQueryParamRef = useRef(!!searchCategory);

  const { categories, loading, promotions } = usePromotionsData(
    searchCategory,
    isInitialLoadWithQueryParamRef.current,
  );

  const activeCategory = useMemo(() => {
    if (!searchCategory || isEmptyCollection(categories)) {
      return ALL_PROMO_ID;
    }
    const isExistingCategory = categories.map(category => category.id).includes(searchCategory);
    return isExistingCategory ? searchCategory : ALL_PROMO_ID;
  }, [searchCategory, categories]);

  const handleCategoryChange = useCallback(
    value => {
      isInitialLoadWithQueryParamRef.current = false;
      navigate(value !== ALL_PROMO_ID ? `${promotionsPath}?category=${value}` : promotionsPath);
    },
    [promotionsPath, navigate],
  );

  const scrollToTopPromotions = useCallback(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, []);

  useEffect(() => {
    Bus.on('ui:scroll:content', scrollToTopPromotions);

    return () => {
      Bus.off('ui:scroll:content', scrollToTopPromotions);
    };
  }, [scrollToTopPromotions]);

  return (
    <PromotionPageLayout>
      <div className="page__main">
        <div className="container-fluid">
          <div className="page__wrapper">
            <div className="tournaments promotions-list-page-wrapper">
              <div className="tournaments__info" ref={scrollRef}>
                <HeaderTitle className="block-lg">
                  <h1 className="header-title__name">{translate('promotions')}</h1>
                </HeaderTitle>

                {categories.length > 0 && (
                  <CategoryMenu
                    activeCategory={activeCategory}
                    categories={categories}
                    onChange={handleCategoryChange}
                  />
                )}
                {categories.length === 0 && <div className="promotions-categories-menu-skeleton" />}
                {loading ? (
                  <div className="promotions-list-wrapper-skeleton">
                    <PromotionsCardSkeleton imageHeight={188} loading />
                  </div>
                ) : (
                  <>
                    {isEmptyCollection(promotions) ? (
                      <div className="promotions-list-empty">
                        <Label
                          className="promotions-list-empty-title"
                          message="promo_empty_title"
                        />
                        <Label
                          className="promotions-list-empty-subtitle"
                          message="promo_empty_text"
                        />
                      </div>
                    ) : (
                      <div className="promotions-list">
                        {promotions.map(promotion => (
                          <PromotionsCard key={promotion.id} promotion={promotion} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SeoPageText />
    </PromotionPageLayout>
  );
};

export default Promotions;
